import { httpService } from './httpService';
import { s3Service } from './s3Service';
import { 
  ChatMessage, 
  Attachment, 
  SenderType, 
  AddChatMessageRequest,
  ViewState
} from '../types/chat';

export const chatService = {
  // Add new chat message using new API
  addChatMessage: async (request: AddChatMessageRequest): Promise<ChatMessage> => {
    try {
      console.log('💬 Adding chat message via API:', request);
      const response = await httpService.addChatMessage(request);
      console.log('✅ Chat message added successfully:', response);
      
      // The API returns the message directly, not wrapped in isSuccess
      if (response && response.id) {
        return {
          id: response.id,
          sender: response.sender,
          timestamp: response.timestamp,
          type: response.type,
          content: response.content,
          attachments: response.attachments || [],
          senderType: response.senderType,
          viewState: response.viewState ?? ViewState.None
        };
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('❌ Error adding chat message:', error);
      throw error;
    }
  },

  // Get chat messages using new API
  getChatMessages: async (tripId: string, pageNumber: number = 1, pageSize: number = 10): Promise<ChatMessage[]> => {
    try {
      console.log('📥 Getting chat messages via API:', { tripId, pageNumber, pageSize });
      const response = await httpService.getChatMessages(tripId, pageNumber, pageSize);
      console.log('✅ Chat messages retrieved successfully:', response);
      
      // The API returns the response directly with messages array
      if (response && response.messages) {
        const computeThumbnailUrl = (url: string): string => {
          try {
            const [base, query] = url.split('?');
            const dotIndex = base.lastIndexOf('.');
            if (dotIndex === -1) return url; // no extension
            const withThumb = `${base.slice(0, dotIndex)}_thumb${base.slice(dotIndex)}`;
            return query ? `${withThumb}?${query}` : withThumb;
          } catch {
            return url;
          }
        };

        const isImageUrl = (url: string): boolean => {
          try {
            const base = url.split('?')[0];
            return /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(base);
          } catch {
            return false;
          }
        };

        return response.messages.map((item: any) => ({
          id: item.id,
          sender: item.sender,
          timestamp: item.timestamp,
          type: item.type,
          content: item.content,
          senderType: item.senderType,
          viewState: item.viewState ?? ViewState.None,
          attachments: (item.attachments || []).map((att: any) => ({
            fileName: att.fileName ?? att.name ?? 'file',
            fileUrl: att.fileUrl ?? att.url ?? '',
            thumbnailUrl: att.thumbnailUrl ?? (att.fileUrl && isImageUrl(att.fileUrl) ? computeThumbnailUrl(att.fileUrl) : undefined)
          }))
        }));
      }
      
      return [];
    } catch (error) {
      console.error('❌ Error getting chat messages:', error);
      return [];
    }
  },

  // Upload file and get URL using S3
  uploadFile: async (file: File, tripId: string): Promise<{ fileUrl: string; thumbnailUrl?: string }> => {
    try {
      console.log('📤 Uploading file to S3:', file.name);
      
      // Upload to S3 and get download URL with thumbnail
      const result = await s3Service.uploadFile(file, tripId);
      
      console.log('✅ File uploaded successfully:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Error uploading file to S3:', error);
      throw error;
    }
  },

  // Send message to backend and return formatted message
  sendMessage: async (
    tripId: string,
    content: string,
    type: 'text' | 'file' | 'image' | 'system' = 'text',
    attachments: Attachment[] = [],
    senderType: number = SenderType.Passenger
  ): Promise<ChatMessage> => {
    try {
      // Prepare attachments for API
      const apiAttachments = attachments.map(attachment => ({
        fileName: attachment.fileName,
        fileUrl: attachment.fileUrl
      }));

      // Send message to backend
      const request: AddChatMessageRequest = {
        tripId,
        content,
        type,
        senderType,
        attachments: apiAttachments,
        viewState: ViewState.Send
      };

      // The API returns the message directly
      const response = await chatService.addChatMessage(request);
      return response;
      
    } catch (error) {
      console.error('❌ Error sending message:', error);
      throw error;
    }
  },

  // Create a new message (for local use)
  createMessage: (
    sender: string,
    content: string,
    type: 'text' | 'file' | 'image' | 'system' = 'text',
    attachments: Attachment[] = [],
    senderType: number = SenderType.Passenger
  ): ChatMessage => {
    return {
      sender,
      timestamp: new Date().toISOString(),
      type,
      content,
      attachments,
      senderType
    };
  }
};
